import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Loader2, ArrowLeft, Save, X, Plane, Globe, ChevronUp, ChevronDown, Eye, Image, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { vehicleGalleryAPI, vehicleCategoriesAPI } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const categoryColors = {
  'all': { bg: 'rgba(0, 240, 255, 0.2)', text: '#00F0FF' },
  'iha-s1': { bg: 'rgba(34, 197, 94, 0.2)', text: '#22C55E' },
  'iha-s2': { bg: 'rgba(168, 85, 247, 0.2)', text: '#A855F7' },
  'prototype': { bg: 'rgba(251, 191, 36, 0.2)', text: '#FBBF24' }
};

// List Component
export const AdminVehicleGallery = () => {
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const { t, language } = useLanguage();

  const fetchData = async () => {
    try {
      const [vehiclesData, categoriesData] = await Promise.all([
        vehicleGalleryAPI.getAll(),
        vehicleCategoriesAPI.getAll()
      ]);
      setVehicles(vehiclesData || []);
      setCategories(categoriesData || []);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    
    try {
      await vehicleGalleryAPI.delete(id);
      toast.success(t('admin.toast.deleted'));
      fetchData();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    }
  };

  const handleReorder = async (id, direction) => {
    setReordering(id);
    try {
      await vehicleGalleryAPI.reorder(id, direction);
      await fetchData();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setReordering(null);
    }
  };

  const getCategoryLabel = (catId) => {
    const category = categories.find(c => c.id === catId);
    if (category) {
      return language === 'tr' && category.name_tr ? category.name_tr : category.name;
    }
    return catId;
  };

  const getCategoryColor = (catId) => {
    const defaultColors = {
      'all': { bg: 'rgba(0, 240, 255, 0.2)', text: '#00F0FF' },
      'iha-s1': { bg: 'rgba(34, 197, 94, 0.2)', text: '#22C55E' },
      'iha-s2': { bg: 'rgba(168, 85, 247, 0.2)', text: '#A855F7' },
      'prototype': { bg: 'rgba(251, 191, 36, 0.2)', text: '#FBBF24' }
    };
    return defaultColors[catId] || { bg: 'rgba(100, 116, 139, 0.2)', text: '#64748B' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-vehicle-gallery">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: '#F8FAFC' }}>
            <Plane size={28} style={{ color: '#00F0FF' }} />
            {t('admin.vehicleGallery.title')}
          </h1>
          <p className="mt-1" style={{ color: '#94A3B8' }}>{t('admin.vehicleGallery.description')}</p>
        </div>
        <Link
          to="/admin/vehicle-gallery/new"
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
            color: '#F8FAFC',
            border: '2px solid #00F0FF',
          }}
        >
          <Plus size={18} />
          {t('admin.vehicleGallery.addNew')}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vehicles.map((vehicle, index) => {
          const catColor = getCategoryColor(vehicle.category);
          return (
            <div key={vehicle.id} className="glass-card overflow-hidden">
              {/* Image */}
              <div className="relative h-48 overflow-hidden" style={{ background: 'rgba(0, 0, 0, 0.3)' }}>
                {vehicle.image ? (
                  <img 
                    src={getImageUrl(vehicle.image)} 
                    alt={vehicle.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Plane size={48} style={{ color: '#64748B' }} />
                  </div>
                )}
                {/* Reorder buttons overlay */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => handleReorder(vehicle.id, 'up')}
                    disabled={index === 0 || reordering === vehicle.id}
                    className="p-1.5 rounded transition-colors disabled:opacity-30"
                    style={{ background: 'rgba(0, 0, 0, 0.6)', color: '#00F0FF' }}
                    data-testid={`reorder-up-${vehicle.id}`}
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    onClick={() => handleReorder(vehicle.id, 'down')}
                    disabled={index === vehicles.length - 1 || reordering === vehicle.id}
                    className="p-1.5 rounded transition-colors disabled:opacity-30"
                    style={{ background: 'rgba(0, 0, 0, 0.6)', color: '#00F0FF' }}
                    data-testid={`reorder-down-${vehicle.id}`}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                {/* Category badge */}
                <div className="absolute bottom-2 left-2">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ background: catColor.bg, color: catColor.text }}
                  >
                    {getCategoryLabel(vehicle.category)}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1" style={{ color: '#F8FAFC' }}>
                  {language === 'tr' && vehicle.name_tr ? vehicle.name_tr : vehicle.name}
                </h3>
                <p className="text-sm line-clamp-2 mb-3" style={{ color: '#94A3B8' }}>
                  {language === 'tr' && vehicle.description_tr ? vehicle.description_tr : vehicle.description}
                </p>
                
                {/* Language indicators */}
                <div className="flex items-center gap-1 mb-4">
                  <span className="px-2 py-0.5 text-xs rounded" style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>EN</span>
                  {vehicle.name_tr && (
                    <span className="px-2 py-0.5 text-xs rounded" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>TR</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-3" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
                  <Link
                    to={`/admin/vehicle-gallery/${vehicle.id}`}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                  >
                    <Edit size={16} />
                  </Link>
                  <button
                    onClick={() => handleDelete(vehicle.id)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {vehicles.length === 0 && (
        <div className="glass-card p-8 text-center" style={{ color: '#94A3B8' }}>
          {t('admin.vehicleGallery.noVehicles')}
        </div>
      )}
    </div>
  );
};

// Form Component
export const AdminVehicleGalleryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { t, language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    name_tr: '',
    mission: '',
    mission_tr: '',
    description: '',
    description_tr: '',
    image: '',
    category: '',
    additionalImages: [],
    videoUrl: '',
    specs: {
      wingspan: '',
      wingspan_tr: '',
      weight: '',
      weight_tr: '',
      mtow: '',
      mtow_tr: '',
      maxSpeed: '',
      maxSpeed_tr: '',
      cruiseSpeed: '',
      cruiseSpeed_tr: '',
      endurance: '',
      endurance_tr: '',
      range: '',
      range_tr: '',
      payload: '',
      payload_tr: '',
      altitude: '',
      altitude_tr: '',
      propulsion: '',
      propulsion_tr: ''
    }
  });

  // Fetch categories first
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await vehicleCategoriesAPI.getAll();
        // Filter out 'all' category as vehicles can't be assigned to 'all'
        const filteredCats = (data || []).filter(c => c.id !== 'all');
        setCategories(filteredCats);
        
        // Set default category if not editing
        if (!isEdit && filteredCats.length > 0) {
          setFormData(prev => ({ ...prev, category: filteredCats[0].id }));
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setLoading(false);
      }
    };
    fetchCategories();
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      const fetchVehicle = async () => {
        try {
          const data = await vehicleGalleryAPI.getById(id);
          setFormData({
            name: data.name || '',
            name_tr: data.name_tr || '',
            mission: data.mission || '',
            mission_tr: data.mission_tr || '',
            description: data.description || '',
            description_tr: data.description_tr || '',
            image: data.image || '',
            category: data.category || 'iha-s1',
            additionalImages: data.additionalImages || [],
            videoUrl: data.videoUrl || '',
            specs: {
              wingspan: data.specs?.wingspan || '',
              wingspan_tr: data.specs?.wingspan_tr || '',
              weight: data.specs?.weight || '',
              weight_tr: data.specs?.weight_tr || '',
              mtow: data.specs?.mtow || '',
              mtow_tr: data.specs?.mtow_tr || '',
              maxSpeed: data.specs?.maxSpeed || '',
              maxSpeed_tr: data.specs?.maxSpeed_tr || '',
              cruiseSpeed: data.specs?.cruiseSpeed || '',
              cruiseSpeed_tr: data.specs?.cruiseSpeed_tr || '',
              endurance: data.specs?.endurance || '',
              endurance_tr: data.specs?.endurance_tr || '',
              range: data.specs?.range || '',
              range_tr: data.specs?.range_tr || '',
              payload: data.specs?.payload || '',
              payload_tr: data.specs?.payload_tr || '',
              altitude: data.specs?.altitude || '',
              altitude_tr: data.specs?.altitude_tr || '',
              propulsion: data.specs?.propulsion || '',
              propulsion_tr: data.specs?.propulsion_tr || ''
            }
          });
        } catch (err) {
          toast.error(t('admin.toast.error'));
          navigate('/admin/vehicle-gallery');
        } finally {
          setLoading(false);
        }
      };
      fetchVehicle();
    }
  }, [id, isEdit, navigate, t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSpecChange = (field, value) => {
    setFormData({
      ...formData,
      specs: { ...formData.specs, [field]: value }
    });
  };

  const handleImageChange = (url) => {
    setFormData({ ...formData, image: url });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await vehicleGalleryAPI.update(id, formData);
        toast.success(t('admin.toast.updated'));
      } else {
        await vehicleGalleryAPI.create(formData);
        toast.success(t('admin.toast.saved'));
      }
      navigate('/admin/vehicle-gallery');
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  // Additional image handlers
  const handleAddImage = (url) => {
    if (url && !formData.additionalImages.includes(url)) {
      setFormData({ ...formData, additionalImages: [...formData.additionalImages, url] });
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.additionalImages.filter((_, i) => i !== index);
    setFormData({ ...formData, additionalImages: newImages });
  };

  const getCategoryLabel = (cat) => {
    return language === 'tr' && cat.name_tr ? cat.name_tr : cat.name;
  };

  return (
    <div data-testid="admin-vehicle-gallery-form">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/vehicle-gallery')}
          className="p-2 rounded-lg"
          style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
          {isEdit ? t('admin.vehicleGallery.editVehicle') : t('admin.vehicleGallery.addNew')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 space-y-6">
        {/* Image and Category */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <ImageUploader
              value={formData.image}
              onChange={handleImageChange}
              label={t('admin.vehicleGallery.form.image')}
              placeholder="Upload vehicle image"
              aspectRatio="16/9"
            />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
              {t('admin.vehicleGallery.form.category')}
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
            >
              {categories.length === 0 ? (
                <option value="">{language === 'tr' ? 'Kategori bulunamadı' : 'No categories found'}</option>
              ) : (
                categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{getCategoryLabel(cat)}</option>
                ))
              )}
            </select>
            {categories.length === 0 && (
              <p className="mt-2 text-sm" style={{ color: '#FBBF24' }}>
                {language === 'tr' 
                  ? '⚠️ Önce Araç Kategorileri sayfasından kategori eklemeniz gerekiyor.' 
                  : '⚠️ You need to add categories from Vehicle Categories page first.'}
              </p>
            )}
          </div>
        </div>

        {/* Additional Images Gallery */}
        <div>
          <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
            <div className="flex items-center gap-2">
              <Image size={16} style={{ color: '#00F0FF' }} />
              {language === 'tr' ? 'Ek Görseller' : 'Additional Images'}
            </div>
          </label>
          <p className="text-xs mb-3" style={{ color: '#94A3B8' }}>
            {language === 'tr' 
              ? 'Araç için ek görseller ekleyin. Bu görseller araç detay sayfasında galeri olarak görünecek.' 
              : 'Add additional images for the vehicle. These will appear as a gallery on the vehicle detail page.'}
          </p>
          
          {/* Current Additional Images */}
          {formData.additionalImages.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {formData.additionalImages.map((img, index) => (
                <div 
                  key={index} 
                  className="relative group rounded-lg overflow-hidden" 
                  style={{ 
                    aspectRatio: '16/9',
                    minHeight: '100px',
                    background: 'rgba(30, 41, 59, 0.6)'
                  }}
                >
                  <img 
                    src={getImageUrl(img)} 
                    alt={`Additional ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: 'center center' }}
                    onError={(e) => {
                      e.target.style.objectFit = 'contain';
                      e.target.style.padding = '8px';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: 'rgba(239, 68, 68, 0.9)', color: 'white' }}
                  >
                    <X size={14} />
                  </button>
                  <div className="absolute bottom-1 left-1 px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}>
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Add New Image */}
          <ImageUploader
            value=""
            onChange={handleAddImage}
            label=""
            placeholder={language === 'tr' ? 'Yeni görsel ekle' : 'Add new image'}
            aspectRatio="16/9"
            showPreview={false}
          />
        </div>

        {/* Language Tabs */}
        <div className="flex items-center gap-2 p-1 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
          <button
            type="button"
            onClick={() => setActiveTab('en')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'en' ? 'rgba(0, 240, 255, 0.2)' : 'transparent',
              color: activeTab === 'en' ? '#00F0FF' : '#94A3B8',
              border: activeTab === 'en' ? '1px solid #00F0FF' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.english')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tr')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'tr' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
              color: activeTab === 'tr' ? '#EF4444' : '#94A3B8',
              border: activeTab === 'tr' ? '1px solid #EF4444' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.turkish')}
          </button>
        </div>

        {/* English Fields */}
        {activeTab === 'en' && (
          <div className="space-y-6" data-testid="en-fields">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                  {t('admin.vehicleGallery.form.name')} (EN) *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  placeholder="e.g., METE-S1"
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                  Mission / Role (EN)
                </label>
                <input
                  type="text"
                  name="mission"
                  value={formData.mission}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  placeholder="e.g., Fixed-Wing Autonomous UAV"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.vehicleGallery.form.description')} (EN)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg resize-none"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Brief description about the vehicle"
              />
            </div>
            
            {/* Video URL */}
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                Video URL (YouTube)
              </label>
              <input
                type="text"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            
            {/* Specs EN */}
            <div>
              <h4 className="font-medium mb-4 flex items-center gap-2" style={{ color: '#00F0FF' }}>
                <Eye size={18} />
                {t('admin.vehicleGallery.form.specs')} (EN)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'wingspan', label: t('admin.vehicleGallery.form.wingspan'), placeholder: '1.8 m' },
                  { key: 'mtow', label: 'MTOW', placeholder: '15 kg' },
                  { key: 'weight', label: t('admin.vehicleGallery.form.weight'), placeholder: '< 35 lbs' },
                  { key: 'cruiseSpeed', label: 'Cruise Speed', placeholder: '45 km/h' },
                  { key: 'maxSpeed', label: t('admin.vehicleGallery.form.maxSpeed'), placeholder: '65 km/h' },
                  { key: 'range', label: 'Range', placeholder: '15 km' },
                  { key: 'endurance', label: t('admin.vehicleGallery.form.endurance'), placeholder: '28 min' },
                  { key: 'payload', label: t('admin.vehicleGallery.form.payload'), placeholder: '2.5 kg' },
                  { key: 'altitude', label: 'Altitude', placeholder: '0-120 m AGL' },
                  { key: 'propulsion', label: t('admin.vehicleGallery.form.propulsion'), placeholder: 'Electric' }
                ].map(spec => (
                  <div key={spec.key}>
                    <label className="block text-xs mb-1" style={{ color: '#94A3B8' }}>{spec.label}</label>
                    <input
                      type="text"
                      value={formData.specs[spec.key]}
                      onChange={(e) => handleSpecChange(spec.key, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        color: '#F8FAFC'
                      }}
                      placeholder={spec.placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Turkish Fields */}
        {activeTab === 'tr' && (
          <div className="space-y-6" data-testid="tr-fields">
            <div className="p-3 rounded-lg mb-4" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <p className="text-sm" style={{ color: '#F87171' }}>
                Türkçe alanları doldurduğunuzda site Türkçe modunda bu içeriği gösterecektir.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                  {t('admin.vehicleGallery.form.name')} (TR)
                </label>
                <input
                  type="text"
                  name="name_tr"
                  value={formData.name_tr}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#F8FAFC'
                  }}
                  placeholder="Örn: METE-S1"
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                  Görev / Rol (TR)
                </label>
                <input
                  type="text"
                  name="mission_tr"
                  value={formData.mission_tr}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#F8FAFC'
                  }}
                  placeholder="Örn: Sabit Kanat Otonom İHA"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.vehicleGallery.form.description')} (TR)
              </label>
              <textarea
                name="description_tr"
                value={formData.description_tr}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg resize-none"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Araç hakkında kısa açıklama"
              />
            </div>
            
            {/* Specs TR */}
            <div>
              <h4 className="font-medium mb-4 flex items-center gap-2" style={{ color: '#EF4444' }}>
                <Eye size={18} />
                {t('admin.vehicleGallery.form.specs')} (TR)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'wingspan_tr', label: t('admin.vehicleGallery.form.wingspan'), placeholder: '1.8 m' },
                  { key: 'mtow_tr', label: 'MTOW', placeholder: '15 kg' },
                  { key: 'weight_tr', label: t('admin.vehicleGallery.form.weight'), placeholder: '< 16 kg' },
                  { key: 'cruiseSpeed_tr', label: 'Seyir Hızı', placeholder: '45 km/sa' },
                  { key: 'maxSpeed_tr', label: t('admin.vehicleGallery.form.maxSpeed'), placeholder: '65 km/sa' },
                  { key: 'range_tr', label: 'Menzil', placeholder: '15 km' },
                  { key: 'endurance_tr', label: t('admin.vehicleGallery.form.endurance'), placeholder: '28 dk' },
                  { key: 'payload_tr', label: t('admin.vehicleGallery.form.payload'), placeholder: '2.5 kg' },
                  { key: 'altitude_tr', label: 'İrtifa', placeholder: '0-120 m YÜS' },
                  { key: 'propulsion_tr', label: t('admin.vehicleGallery.form.propulsion'), placeholder: 'Elektrik' }
                ].map(spec => (
                  <div key={spec.key}>
                    <label className="block text-xs mb-1" style={{ color: '#94A3B8' }}>{spec.label}</label>
                    <input
                      type="text"
                      value={formData.specs[spec.key]}
                      onChange={(e) => handleSpecChange(spec.key, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        color: '#F8FAFC'
                      }}
                      placeholder={spec.placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-4 pt-6" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.2)' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/vehicle-gallery')}
            className="px-6 py-3 rounded-lg"
            style={{ color: '#94A3B8', border: '1px solid #94A3B8' }}
          >
            {t('admin.common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium"
            style={{
              background: 'linear-gradient(90deg, #00F0FF 0%, #06B6D4 100%)',
              color: '#0F172A',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {isEdit ? t('admin.common.update') : t('admin.common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminVehicleGallery;
